import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { Subject } from 'rxjs';
import { Store } from '@ngrx/store';
import * as fromRoot from '../../../../../state/app.state';
import { ActivatedRoute, Router } from '@angular/router';
import { FetchSiteDetailsRequest } from '../../../core/projects.actions';
import { siteDetails } from '../../../core/projects.selectors';
import { takeUntil } from 'rxjs/operators';
import { MatTabGroup } from '@angular/material/tabs';
import { ProjectsService } from '../../../core/projects.service';
import { ConfirmDialogComponent, ConfirmDialogModel } from '../../../../../shared/components';
import { MatDialog as MatDialog } from '@angular/material/dialog';
import * as moment from 'moment/moment';
import { CommonService } from '../../../../../shared/services/common.service';
import { AuthService } from '../../../../../shared/services/auth.service';

@Component({
    selector: 'app-site-details-screen',
    templateUrl: './site-details-screen.component.html',
    styleUrls: ['./site-details-screen.component.scss'],
    standalone: false
})
export class SiteDetailsScreenComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('tabGroup') tabs: MatTabGroup;
  @ViewChild('siteHeading') siteHeadingEl: ElementRef<HTMLElement>;
  @ViewChild('siteSubHeading') siteSubHeadingEl: ElementRef<HTMLElement>;
  private readonly authService = inject(AuthService);
  siteDetails: any = {};
  headingEllipsis = false;
  subHeadingEllipsis = false;
  // Diaries moved to the project page's Diaries tab, so this site tab bar is
  // back to Daily report + Workflow.
  tabIndexMap = {
    'daily-report': 0,
    workflow: 1,
  };
  selectedTabIndex = 0;
  reportId: any;
  projectId: any;
  date: any;
  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    private store: Store<fromRoot.State>,
    private router: Router,
    private route: ActivatedRoute,
    private projectsService: ProjectsService,
    private commonService: CommonService,
    private cdr: ChangeDetectorRef,
    public dialog: MatDialog,
  ) {
    this.store.dispatch(
      FetchSiteDetailsRequest({
        payload: {
          siteId: this.route.snapshot.params.siteId,
          qp: {
            include: [
              'geometry',
              {
                project: [
                  'cover_photo',
                  'owner_organization',
                ],
              },
              {
                headings: [
                  'stations',
                ],
              },
            ],
          },
        },
      }),
    );

    this.route.queryParams
      .pipe(takeUntil(this.onDestroy))
      .subscribe(qp => {
        if (qp.fs === undefined) {
          if (qp.tab) {
            this.selectedTabIndex = this.tabIndexMap[qp.tab] || 0;
          } else {
            this.router.navigate([], {
              queryParams: {
                tab: 'daily-report',
              },
            });
          }

          if (qp?.['reportId']) {
            this.reportId = qp?.['reportId'];
          } else {
            this.reportId = null;
          }

          if (qp.date && moment(qp.date, 'YYYY-MM-DD').isValid()) {
            this.date = qp.date;
          }
        }
      });
  }

  ngOnInit() {
    // Project-level permission flags can change while a session is open; pull
    // fresh CASL rules on entry so report editability reflects the current state.
    this.authService.refreshAbilities();

    this.store.select(siteDetails)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(site => {
        this.siteDetails = site;
        setTimeout(() => this.updateEllipsisState());
      });

    this.commonService.removeReportId
      .pipe(takeUntil(this.onDestroy))
      .subscribe((data) => {
        if (data) {
          this.router.navigate([], {
            queryParams: {
              reportId: null,
              editable: 0,
            },
            queryParamsHandling: 'merge',
          });
        }
      });
  }

  ngAfterViewInit() {
    // Disable tab click, to change the tab. This is done to make the tab
    // change stay in sync with the query params.
    this.tabs._handleClick = this.onTabChange?.bind(this);

    setTimeout(() => {
      this.updateEllipsisState();
    });
  }

  private updateEllipsisState() {
    const heading = this.siteHeadingEl?.nativeElement;
    const sub = this.siteSubHeadingEl?.nativeElement;
    this.headingEllipsis = heading ? heading.scrollWidth > heading.clientWidth : false;
    this.subHeadingEllipsis = sub ? sub.scrollWidth > sub.clientWidth : false;
    this.cdr.detectChanges();
  }

  onTabChange(tab) {
    const isFormDirty = this.projectsService.isAnyFormDirty.getValue();
    const currentParams = { ...this.route.snapshot.queryParams };
    const tabName = tab.textLabel.toLowerCase().replaceAll(' ', '-');

    if (isFormDirty) {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        disableClose: true,
        data: new ConfirmDialogModel(
          'Unsaved changes',
          `There are unsaved changes.<br/>Are you sure you want to discard?`,
        ),
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          this.router.navigate([], {
            queryParams: {
              ...currentParams,
              tab: tabName,
            },
            queryParamsHandling: 'merge',
          }).then(() => {
            this.projectsService.isAnyFormDirty.next(false);
          });
        }
      });
    } else {
      // No dirty form, navigate directly
      this.router.navigate([], {
        queryParams: {
          ...currentParams,
          tab: tabName,
        },
        queryParamsHandling: 'merge',
      });
    }
  }


  navigateToProject() {
    const projectId = this.route.snapshot.params.projectId;
    this.router.navigate([`/projects/${projectId}`]);
  }

  ngOnDestroy() {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
